/**
 * Comprehensive Authentication Logger
 * 
 * Provides structured logging for authentication flow debugging.
 * All logs are prefixed with session IDs for easy filtering.
 * Supports debug mode toggle for verbose console logging.
 */

import { supabase } from '@/integrations/supabase/client';

type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'CRITICAL';

interface AuthLogEntry {
  level: LogLevel;
  event: string;
  data?: Record<string, any>;
  timestamp: string;
  sessionId: string;
}

// Generate unique session ID for this browser tab
const SESSION_ID = `auth-${Math.random().toString(36).substring(2, 10)}`;

// Debug mode state - stored in localStorage for persistence
const DEBUG_MODE_KEY = 'auth_debug_mode';
let debugModeEnabled = localStorage.getItem(DEBUG_MODE_KEY) === 'true';

// Remote logging state
const REMOTE_LOGGING_KEY = 'auth_remote_logging';
let remoteLoggingEnabled = localStorage.getItem(REMOTE_LOGGING_KEY) === 'true';

// State machine for tracking auth transitions
type AuthState = 
  | 'UNKNOWN'
  | 'CHECKING_CACHE'
  | 'CACHE_HIT'
  | 'CACHE_MISS'
  | 'VALIDATING_SERVER'
  | 'AUTHENTICATED'
  | 'UNAUTHENTICATED'
  | 'ERROR';

let currentAuthState: AuthState = 'UNKNOWN';

/**
 * Toggle debug mode for verbose console logging
 */
const setDebugMode = (enabled: boolean) => {
  debugModeEnabled = enabled;
  localStorage.setItem(DEBUG_MODE_KEY, String(enabled));
  console.log(`[AUTH] Debug mode ${enabled ? 'ENABLED' : 'DISABLED'}`);
};

/**
 * Toggle remote logging to database
 */
const setRemoteLogging = (enabled: boolean) => {
  remoteLoggingEnabled = enabled;
  localStorage.setItem(REMOTE_LOGGING_KEY, String(enabled));
  console.log(`[AUTH] Remote logging ${enabled ? 'ENABLED' : 'DISABLED'}`);
};

/**
 * Check if debug mode is enabled
 */
const isDebugMode = () => debugModeEnabled;

/**
 * Check if remote logging is enabled
 */
const isRemoteLoggingEnabled = () => remoteLoggingEnabled;

/**
 * Mask email for privacy in logs
 */
const maskEmail = (email: string | null | undefined): string => {
  if (!email) return 'null';
  const [local, domain] = email.split('@');
  if (!domain) return '***';
  const maskedLocal = local.length > 2 
    ? `${local[0]}${'*'.repeat(Math.min(local.length - 2, 4))}${local[local.length - 1]}`
    : '**';
  return `${maskedLocal}@${domain}`;
};

/**
 * Get token fingerprint (last 8 chars) for debugging
 */
const getTokenFingerprint = (token: string | null | undefined): string => {
  if (!token) return 'null';
  if (token.length < 12) return '***';
  return `...${token.slice(-8)}`;
};

/**
 * Format log message with consistent structure
 */
const formatLog = (entry: AuthLogEntry): string => {
  return `[${entry.sessionId}] [${entry.timestamp}] [${entry.level}] ${entry.event}`;
};

/**
 * Send log to remote database
 */
const sendToRemote = async (entry: AuthLogEntry, maskedData?: Record<string, any>) => {
  if (!remoteLoggingEnabled) return;
  
  try {
    await (supabase as any).from('auth_debug_logs').insert({
      session_id: entry.sessionId,
      log_level: entry.level,
      event_type: entry.event.split(' ')[0] || entry.event,
      message: entry.event,
      details: maskedData || null,
      user_email_masked: maskedData?.email || maskedData?.userEmail || null,
      client_timestamp: entry.timestamp,
      user_agent: navigator.userAgent
    });
  } catch (error) {
    // Silently fail - don't break auth flow for logging
    console.debug('[AUTH] Failed to send remote log:', error);
  }
};

/**
 * Main logging function
 */
const log = (level: LogLevel, event: string, data?: Record<string, any>) => {
  const entry: AuthLogEntry = {
    level,
    event,
    data,
    timestamp: new Date().toISOString(),
    sessionId: SESSION_ID
  };

  const message = formatLog(entry);
  const logData = data ? { ...data } : undefined;

  // Mask sensitive data
  if (logData?.email) {
    logData.email = maskEmail(logData.email);
  }
  if (logData?.token) {
    logData.token = getTokenFingerprint(logData.token);
  }
  if (logData?.userEmail) {
    logData.userEmail = maskEmail(logData.userEmail);
  }

  // Only log DEBUG level if debug mode is enabled
  if (level === 'DEBUG' && !debugModeEnabled) {
    // Still store in session storage for retrieval
    storeLog(entry, logData);
    return;
  }

  switch (level) {
    case 'DEBUG':
      console.debug(message, logData || '');
      break;
    case 'INFO':
      if (debugModeEnabled) {
        console.log(message, logData || '');
      }
      break;
    case 'WARN':
      console.warn(message, logData || '');
      break;
    case 'ERROR':
    case 'CRITICAL':
      console.error(message, logData || '');
      break;
  }

  // Store logs and send critical ones to remote
  storeLog(entry, logData);
  
  // Send to remote for ERROR and CRITICAL
  if (level === 'ERROR' || level === 'CRITICAL') {
    sendToRemote(entry, logData);
  } else if (remoteLoggingEnabled && (level === 'INFO' || level === 'WARN')) {
    // Also send INFO/WARN if remote logging is explicitly enabled
    sendToRemote(entry, logData);
  }
};

/**
 * Store log in session storage
 */
const storeLog = (entry: AuthLogEntry, logData?: Record<string, any>) => {
  try {
    const storedLogs = JSON.parse(sessionStorage.getItem('auth_error_logs') || '[]');
    storedLogs.push({ ...entry, data: logData });
    // Keep only last 100 logs
    if (storedLogs.length > 100) storedLogs.shift();
    sessionStorage.setItem('auth_error_logs', JSON.stringify(storedLogs));
  } catch {
    // Ignore storage errors
  }
};

/**
 * Log state transitions
 */
const logStateTransition = (from: AuthState, to: AuthState, reason?: string) => {
  currentAuthState = to;
  log('INFO', `[STATE] ${from} -> ${to}`, reason ? { reason } : undefined);
};

/**
 * Get current auth state
 */
const getState = (): AuthState => currentAuthState;

/**
 * Auth Logger API
 */
export const authLogger = {
  // Session info
  sessionId: SESSION_ID,
  getState,

  // Debug mode controls
  setDebugMode,
  isDebugMode,
  setRemoteLogging,
  isRemoteLoggingEnabled,

  // Log levels
  debug: (event: string, data?: Record<string, any>) => log('DEBUG', event, data),
  info: (event: string, data?: Record<string, any>) => log('INFO', event, data),
  warn: (event: string, data?: Record<string, any>) => log('WARN', event, data),
  error: (event: string, data?: Record<string, any>) => log('ERROR', event, data),
  critical: (event: string, data?: Record<string, any>) => log('CRITICAL', event, data),

  // State transitions
  transition: logStateTransition,

  // Convenience methods for common auth events
  loginStart: (email: string, mode: 'signin' | 'signup') => {
    log('INFO', 'Login flow started', { email, mode });
  },

  otpSent: (email: string) => {
    log('INFO', 'OTP sent successfully', { email });
  },

  otpVerifyStart: (email: string) => {
    log('INFO', 'OTP verification started', { email });
  },

  otpVerifySuccess: (email: string, hasElevatedAccess: boolean) => {
    log('INFO', 'OTP verified successfully', { email, hasElevatedAccess });
  },

  otpVerifyFailed: (email: string, reason: string) => {
    log('WARN', 'OTP verification failed', { email, reason });
  },

  tokenStored: (type: 'admin' | 'user', email: string) => {
    log('INFO', 'Token stored in localStorage', { type, email });
  },

  cachePopulated: (context: 'admin' | 'user', email: string) => {
    log('INFO', 'Validation cache pre-populated', { context, email });
  },

  validationStart: (context: 'admin' | 'user') => {
    logStateTransition(currentAuthState, 'CHECKING_CACHE', `context: ${context}`);
    log('DEBUG', 'Token validation started', { context });
  },

  cacheHit: (context: 'admin' | 'user', email: string | null, cacheAge: number) => {
    logStateTransition(currentAuthState, 'CACHE_HIT');
    log('DEBUG', 'Cache hit - using cached result', { context, email, cacheAgeMs: cacheAge });
  },

  cacheMiss: (context: 'admin' | 'user', reason: string) => {
    logStateTransition(currentAuthState, 'CACHE_MISS', reason);
    log('DEBUG', 'Cache miss - will validate with server', { context, reason });
  },

  serverValidationStart: (context: 'admin' | 'user') => {
    logStateTransition(currentAuthState, 'VALIDATING_SERVER');
    log('DEBUG', 'Server validation request sent', { context });
  },

  serverValidationSuccess: (email: string, isAdmin: boolean) => {
    logStateTransition(currentAuthState, 'AUTHENTICATED', email);
    log('INFO', 'Server validation successful', { email, isAdmin });
  },

  serverValidationFailed: (reason: string) => {
    logStateTransition(currentAuthState, 'UNAUTHENTICATED', reason);
    log('WARN', 'Server validation failed', { reason });
  },

  sessionMismatch: (expected: string | null, actual: string | null) => {
    log('CRITICAL', 'Session mismatch detected!', { 
      expected, 
      actual,
      action: 'Clearing stale tokens'
    });
  },

  tokenCleared: (reason: string) => {
    logStateTransition(currentAuthState, 'UNAUTHENTICATED', reason);
    log('INFO', 'Authentication tokens cleared', { reason });
  },

  healthCheckPassed: (context: 'admin' | 'user') => {
    log('DEBUG', 'Session health check passed', { context });
  },

  healthCheckFailed: (context: 'admin' | 'user', reason: string) => {
    log('WARN', 'Session health check failed', { context, reason });
  },

  storageEvent: (key: string, hasValue: boolean) => {
    log('DEBUG', 'Storage event received', { key, hasValue });
  },

  contextStateUpdate: (isAuthenticated: boolean, email: string | null) => {
    log('DEBUG', 'AuthContext state updated', { isAuthenticated, email });
  },

  // Role-related logging methods
  rolesFetchStart: (profileId: string) => {
    log('DEBUG', '[ROLES] Fetch started', { profileId });
  },

  rolesFetchSuccess: (profileId: string, roles: string[], isAdmin: boolean) => {
    log('INFO', '[ROLES] Fetch successful', { profileId, roles, isAdmin, roleCount: roles.length });
  },

  rolesFetchFailed: (profileId: string, error: string) => {
    log('ERROR', '[ROLES] Fetch failed', { profileId, error });
  },

  adminAccessCheck: (result: { isReady: boolean; isAdmin: boolean; profileId: string | null; roles: string[]; source: string }) => {
    log('DEBUG', '[ADMIN] Access check', result);
  },

  adminAccessGranted: (email: string, roles: string[]) => {
    log('INFO', '[ADMIN] Access granted', { email, roles });
  },

  adminAccessDenied: (email: string | null, reason: string) => {
    log('WARN', '[ADMIN] Access denied', { email, reason });
  },

  // Utility to get recent error logs
  getRecentErrors: (): AuthLogEntry[] => {
    try {
      return JSON.parse(sessionStorage.getItem('auth_error_logs') || '[]');
    } catch {
      return [];
    }
  },

  // Get all stored logs (including debug)
  getAllLogs: (): AuthLogEntry[] => {
    try {
      return JSON.parse(sessionStorage.getItem('auth_error_logs') || '[]');
    } catch {
      return [];
    }
  },

  // Clear stored logs
  clearLogs: () => {
    sessionStorage.removeItem('auth_error_logs');
  },

  // Helper utilities
  maskEmail,
  getTokenFingerprint
};

export default authLogger;
